<section class="prq" aria-labelledby="prq-title">
  <h3 class="prq__title" id="prq-title">{{ copy.title }}</h3>

  <div class="prq__toolbar">
    <div class="prq__tabs" role="group" [attr.aria-label]="copy.boxLabel">
      <button
        type="button"
        class="prq__tab"
        [class.is-active]="box() === 'inbox'"
        [attr.aria-pressed]="box() === 'inbox'"
        (click)="selectBox('inbox')"
      >
        {{ inboxLabel() }}
      </button>
      <button
        type="button"
        class="prq__tab"
        [class.is-active]="box() === 'sent'"
        [attr.aria-pressed]="box() === 'sent'"
        (click)="selectBox('sent')"
      >
        {{ copy.sent }}
      </button>
    </div>

    <div class="prq__filter">
      <label class="prq__filter-label" for="prq-status">{{ copy.statusFilterLabel }}</label>
      <select id="prq-status" class="prq__select" [value]="status() ?? ''" (change)="onStatusChange($event)">
        <option value="">{{ copy.allStatuses }}</option>
        @for (option of statuses; track option) {
          <option [value]="option">{{ copy.statusLabels[option] }}</option>
        }
      </select>
    </div>
  </div>

  @if (notice(); as n) {
    <p class="prq__notice" [class.prq__notice--error]="n.tone === 'error'" role="status">{{ n.text }}</p>
  }

  @if (loading()) {
    <div class="prq__skeleton" role="status" aria-busy="true">
      <span class="prq__sr-only">{{ copy.skeletonLabel }}</span>
      @for (row of skeletonRows; track row) {
        <span class="prq__skeleton-row" aria-hidden="true"></span>
      }
    </div>
  } @else if (failed()) {
    <div class="prq__error" role="alert">
      <p class="prq__error-text">{{ copy.loadError }}</p>
      <button type="button" class="prq__secondary" (click)="retry()">{{ copy.retry }}</button>
    </div>
  } @else if (items().length === 0) {
    <div class="prq__empty">
      @if (!emptyMascotError()) {
        <img
          src="images/logo/comunity.png"
          [alt]="copy.emptyMascotAlt"
          class="prq__mascot"
          loading="lazy"
          (error)="onEmptyMascotError()"
        />
      }
      <p class="prq__empty-text">{{ emptyText() }}</p>
    </div>
  } @else {
    <ul class="prq__list">
      @for (item of items(); track item.id) {
        <li class="prq__item" [attr.data-request-id]="item.id">
          <div class="prq__head">
            @if (item.counterpart.available) {
              <div class="prq__avatar" aria-hidden="true">{{ initials(item.counterpart.alias) }}</div>
              <div class="prq__who">
                <span class="prq__alias">{{ item.counterpart.alias }}</span>
                <span class="prq__zone">{{ location(item) }}</span>
              </div>
            } @else {
              <div class="prq__who">
                @if (item.counterpart.alias) {
                  <span class="prq__alias">{{ item.counterpart.alias }}</span>
                }
                <span class="prq__unavailable">{{ copy.unavailable }}</span>
              </div>
            }
            <span class="prq__status" [attr.data-status]="item.status">{{ statusLabel(item) }}</span>
          </div>

          @if (closedByText(item); as closed) {
            <p class="prq__closed-by">{{ closed }}</p>
          }

          @if (item.counterpart.available && item.counterpart.games.length > 0) {
            <ul class="prq__games">
              @for (game of item.counterpart.games; track game.slug) {
                <li class="prq__game" [style.--pill-color]="game.accent_color">{{ game.name }}</li>
              }
            </ul>
          }

          @if (item.game) {
            <p class="prq__meta">{{ copy.inboxGame(item.game.name) }}</p>
          }
          @if (item.message) {
            <!-- Texto plano: interpolación, nunca [innerHTML] (§5.6 4 bis, MK7). -->
            <p class="prq__message">{{ item.message }}</p>
          }

          <p class="prq__dates">
            {{ copy.createdAt(date(item.created_at)) }}
            @if (item.responded_at) {
              · {{ copy.respondedAt(date(item.responded_at)) }}
            }
          </p>

          @if (contactView(item); as contact) {
            <div class="prq__contact">
              <span class="prq__contact-title">{{ copy.contactTitle }} · {{ copy.contactMethodLabels[item.contact!.method] }}</span>
              @if (contact.kind === 'link') {
                <a class="prq__contact-value" [href]="contact.href" target="_blank" rel="noopener noreferrer">{{ contact.text }}</a>
              } @else {
                <span class="prq__contact-value">{{ contact.text }}</span>
              }
            </div>
          }

          @if (item.can.accept || item.can.reject || item.can.cancel || item.can.close) {
            <div class="prq__actions">
              @if (item.can.accept) {
                <button type="button" class="prq__primary" [disabled]="isBusy(item.id)" (click)="run(item, 'accept')">
                  {{ copy.accept }}
                </button>
              }
              @if (item.can.reject) {
                <button type="button" class="prq__secondary" [disabled]="isBusy(item.id)" (click)="run(item, 'reject')">
                  {{ copy.reject }}
                </button>
              }
              @if (item.can.cancel) {
                <button type="button" class="prq__secondary" [disabled]="isBusy(item.id)" (click)="run(item, 'cancel')">
                  {{ copy.cancelRequest }}
                </button>
              }
              @if (item.can.close) {
                @if (confirmingClose() === item.id) {
                  <div class="prq__confirm" role="alertdialog" [attr.aria-labelledby]="'prq-confirm-' + item.id">
                    <p class="prq__confirm-text" [id]="'prq-confirm-' + item.id">{{ copy.closeConfirm }}</p>
                    <div class="prq__confirm-actions">
                      <button type="button" class="prq__danger" [disabled]="isBusy(item.id)" (click)="run(item, 'close')">
                        {{ copy.closeYes }}
                      </button>
                      <button
                        #closeCancelButton
                        type="button"
                        class="prq__secondary"
                        [attr.data-id]="item.id"
                        (click)="cancelClose(item)"
                      >
                        {{ copy.closeNo }}
                      </button>
                    </div>
                  </div>
                } @else {
                  <button
                    #closeButton
                    type="button"
                    class="prq__secondary"
                    [attr.data-id]="item.id"
                    [disabled]="isBusy(item.id)"
                    (click)="askClose(item)"
                  >
                    {{ copy.close }}
                  </button>
                }
              }
            </div>
          }
        </li>
      }
    </ul>

    @if (meta(); as m) {
      @if (m.total_pages > 1) {
        <div class="prq__pagination">
          <app-pagination [currentPage]="m.page" [totalPages]="m.total_pages" (pageChange)="goToPage($event)" />
        </div>
      }
    }
  }

  <p class="prq__report">
    <a [routerLink]="contactPath">{{ reportCopy }}</a>
  </p>
</section>
